import React from "react";
import { Menu, LogOut, Sun, Moon } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../lib/theme";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Avatar, AvatarFallback } from "./ui/avatar";
import { PendenciaDot } from "./pendenciaDocsContext";
import { Badge } from "./ui/badge";
import { cedentePath } from "../lib/portal";

const ROLE_LABEL = {
  cedente_admin: "Admin Cedente",
  cedente_operator: "Operador",
  cedente_viewer: "Visualizador",
};

export default function CedenteHeader({ onOpenSidebar }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useTheme();

  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <header className="sticky top-0 z-20 bg-white/80 backdrop-blur-md border-b border-[var(--zemo-border)]">
      <div className="h-16 px-4 lg:px-8 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            className="lg:hidden p-2 -ml-2 rounded hover:bg-zinc-100"
            onClick={onOpenSidebar}
            data-testid="ced-open-sidebar-button"
            aria-label="Abrir menu"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div className="hidden md:block">
            <div className="text-[12px] text-zinc-500">
              Operando via parceiro <span className="font-semibold text-zinc-700">{user?.partner_name}</span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            className="p-2 rounded hover:bg-zinc-100"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            data-testid="ced-theme-toggle-btn"
            aria-label="Alternar tema"
            title={theme === "dark" ? "Tema claro" : "Tema escuro"}
          >
            {theme === "dark" ? <Sun className="h-4 w-4 text-zinc-600" /> : <Moon className="h-4 w-4 text-zinc-600" />}
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2.5 pl-1 pr-3 py-1 rounded-md hover:bg-zinc-50 border border-transparent hover:border-[var(--zemo-border)] transition-colors"
                data-testid="ced-user-menu-trigger"
              >
                <span className="relative inline-flex">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-emerald-700 text-white text-xs font-semibold">
                      {initials}
                    </AvatarFallback>
                  </Avatar>
                  <PendenciaDot testid="ced-user-menu-pendencia-dot" className="absolute -top-0.5 -right-0.5 ring-2 ring-white" />
                </span>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-[13px] font-semibold text-zinc-900">{user?.name}</div>
                  <div className="text-[11px] text-zinc-500">{ROLE_LABEL[user?.role] || user?.role}</div>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuLabel className="flex flex-col gap-1">
                <span className="font-semibold">{user?.name}</span>
                <span className="text-xs text-zinc-500 font-normal">{user?.email}</span>
                <div className="flex items-center gap-1.5 mt-1">
                  <Badge variant="secondary" className="text-[10px] uppercase tracking-wider">
                    {ROLE_LABEL[user?.role] || user?.role}
                  </Badge>
                  {user?.two_factor_enabled && (
                    <Badge variant="outline" className="text-[10px] text-emerald-700 border-emerald-200 bg-emerald-50">
                      2FA ativo
                    </Badge>
                  )}
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate(cedentePath("/configuracoes"))} data-testid="ced-menu-settings">
                Configurações
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={async () => {
                  await logout();
                  navigate(cedentePath("/login"));
                }}
                className="text-red-600 focus:text-red-700 focus:bg-red-50"
                data-testid="ced-menu-logout"
              >
                <LogOut className="h-4 w-4 mr-2" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </header>
  );
}
